import { useEffect, useMemo, useState } from 'react'

const KEY = 'bv_cart_v1'

export type CartItem = {
  productId: number
  name: string
  unitPrice: number
  quantity: number
  /** SMM package quantity (followers count). */
  smmPackageQuantity?: number
  /** Delivery target URL (cart field name kept for compatibility). */
  smmTiktokTarget?: string
  smmPlatform?: import('#bv/data/products').SmmPlatform
  smmServiceId?: number
  smmTierId?: string
  /** Custom Robux order — desired amount after tax. */
  robuxAmount?: number
  /** Gamepass price the buyer must set (R$). */
  robuxGamepassPrice?: number
  /** Roblox gamepass URL for fulfillment. */
  robuxGamepassLink?: string
  /** Discord server boost count for custom listing. */
  serverBoostCount?: number
  /** Discord invite URL for boost fulfillment. */
  serverBoostInviteLink?: string
}

function cartLineKey(
  i: Pick<
    CartItem,
    | 'productId'
    | 'unitPrice'
    | 'smmPackageQuantity'
    | 'smmTiktokTarget'
    | 'smmTierId'
    | 'robuxAmount'
    | 'robuxGamepassPrice'
    | 'robuxGamepassLink'
    | 'serverBoostCount'
    | 'serverBoostInviteLink'
  >,
): string {
  return [
    i.productId,
    i.unitPrice,
    i.smmPackageQuantity ?? '',
    i.smmTierId ?? '',
    i.smmTiktokTarget ?? '',
    i.robuxAmount ?? '',
    i.robuxGamepassPrice ?? '',
    i.robuxGamepassLink ?? '',
    i.serverBoostCount ?? '',
    i.serverBoostInviteLink ?? '',
  ].join(':')
}

function read(): CartItem[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as CartItem[]
    if (!Array.isArray(parsed)) return []
    return parsed.filter((i) => Number.isFinite(i.productId) && Number.isFinite(i.unitPrice) && i.quantity > 0)
  } catch {
    return []
  }
}

function write(items: CartItem[]) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(KEY, JSON.stringify(items))
  window.dispatchEvent(new Event('bv_cart_changed'))
}

export function addToCart(item: Omit<CartItem, 'quantity'>, quantity = 1) {
  const items = read()
  const key = cartLineKey(item)
  const idx = items.findIndex((i) => cartLineKey(i) === key)
  if (idx >= 0) {
    items[idx] = { ...items[idx], quantity: items[idx].quantity + Math.max(1, quantity) }
  } else {
    items.push({ ...item, quantity: Math.max(1, quantity) })
  }
  write(items)
}

export function clearCart() {
  write([])
}

/** Replace cart contents (e.g. express “buy now” with a single line). */
export function setCartItems(items: CartItem[]) {
  write(items.filter((i) => Number.isFinite(i.productId) && Number.isFinite(i.unitPrice) && i.quantity > 0))
}

/** Payload for checkout server functions (card, crypto, manual F&F). */
export function cartItemsForCheckout(items: CartItem[]) {
  return items.map((item) => ({
    productId: item.productId,
    name: item.name,
    unitPrice: item.unitPrice,
    quantity: item.quantity,
    ...(item.smmPackageQuantity != null ? { smmPackageQuantity: item.smmPackageQuantity } : {}),
    ...(item.smmTiktokTarget ? { smmTiktokTarget: item.smmTiktokTarget } : {}),
    ...(item.smmServiceId != null ? { smmServiceId: item.smmServiceId } : {}),
    ...(item.smmTierId ? { smmTierId: item.smmTierId } : {}),
    ...(item.robuxAmount != null ? { robuxAmount: item.robuxAmount } : {}),
    ...(item.robuxGamepassPrice != null ? { robuxGamepassPrice: item.robuxGamepassPrice } : {}),
    ...(item.robuxGamepassLink ? { robuxGamepassLink: item.robuxGamepassLink } : {}),
    ...(item.serverBoostCount != null ? { serverBoostCount: item.serverBoostCount } : {}),
    ...(item.serverBoostInviteLink ? { serverBoostInviteLink: item.serverBoostInviteLink } : {}),
  }))
}

export function isFixedPackageCartLine(item: CartItem): boolean {
  return item.smmPackageQuantity != null || item.robuxAmount != null || item.serverBoostCount != null
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>([])

  useEffect(() => {
    const sync = () => setItems(read())
    sync()
    window.addEventListener('storage', sync)
    window.addEventListener('bv_cart_changed', sync)
    return () => {
      window.removeEventListener('storage', sync)
      window.removeEventListener('bv_cart_changed', sync)
    }
  }, [])

  const count = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items])
  const subtotal = useMemo(() => items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0), [items])

  const remove = (
    productId: number,
    unitPrice: number,
    smmPackageQuantity?: number,
    smmTiktokTarget?: string,
    smmTierId?: string,
    robuxAmount?: number,
    robuxGamepassPrice?: number,
    robuxGamepassLink?: string,
    serverBoostCount?: number,
    serverBoostInviteLink?: string,
  ) => {
    const key = cartLineKey({
      productId,
      unitPrice,
      smmPackageQuantity,
      smmTiktokTarget,
      smmTierId,
      robuxAmount,
      robuxGamepassPrice,
      robuxGamepassLink,
      serverBoostCount,
      serverBoostInviteLink,
    })
    write(items.filter((i) => cartLineKey(i) !== key))
  }
  const setQty = (
    productId: number,
    unitPrice: number,
    quantity: number,
    smmPackageQuantity?: number,
    smmTiktokTarget?: string,
    smmTierId?: string,
    robuxAmount?: number,
    robuxGamepassPrice?: number,
    robuxGamepassLink?: string,
    serverBoostCount?: number,
    serverBoostInviteLink?: string,
  ) => {
    const key = cartLineKey({
      productId,
      unitPrice,
      smmPackageQuantity,
      smmTiktokTarget,
      smmTierId,
      robuxAmount,
      robuxGamepassPrice,
      robuxGamepassLink,
      serverBoostCount,
      serverBoostInviteLink,
    })
    const q = Math.max(1, quantity)
    write(items.map((i) => (cartLineKey(i) === key ? { ...i, quantity: q } : i)))
  }

  return { items, count, subtotal, remove, setQty, clear: clearCart }
}
